import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { selectHierarchicalRegistrationStatusMap } from "@/data-preparation-tool/store/registration-status-selectors";
import { selectEditMode } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-selectors";
import { doesEntityRequireReview } from "@/data-preparation-tool/utils/entity-utils";
import { useAppSelector } from "@/store/store-hooks";
import { ChangeIndicatorBadge, yellow } from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntity,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
  isCaptureTreeEntityRevision,
  isRevisionScanEntity,
  RegistrationStatus,
} from "@faro-lotv/service-wires";
import { SxProps } from "@mui/system";
import { useMemo, type JSX } from "react";

interface EntityIconProps {
  /** entity to show the icon for */
  entity: CaptureTreeEntity;

  /** styles to apply to the icon */
  sx: SxProps;
}

/** @returns icon for a capture tree entity */
export function EntityIcon({ entity, sx }: EntityIconProps): JSX.Element {
  const statusMap = useAppSelector(selectHierarchicalRegistrationStatusMap);

  const requiresReview = useMemo(() => {
    if (
      isCaptureTreeEntityRevision(entity) &&
      (isRevisionScanEntity(entity) ||
        entity.type === CaptureTreeEntityType.cluster)
    ) {
      return doesEntityRequireReview(entity, statusMap);
    }
    return false;
  }, [entity, statusMap]);

  return (
    <CaptureTreeEntityIcon
      sx={sx}
      badgeVisible={requiresReview}
      entity={entity}
    />
  );
}

type ScanEntityIconProps = EntityIconProps & {
  /** whether the badge should be visible */
  badgeVisible: boolean;
};

/** @returns the label component used, for each node, in the tree with the node's icon, name and context menu */
function CaptureTreeEntityIcon({
  sx,
  badgeVisible,
  entity,
}: ScanEntityIconProps): JSX.Element {
  const isEditModeEnabled = useAppSelector(selectEditMode) !== undefined;
  const iconType = iconForCaptureTreeElement(entity, isEditModeEnabled);
  return (
    <ChangeIndicatorBadge
      color={yellow[600]}
      overlap="circular"
      invisible={!badgeVisible}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      // Inline style, not `sx`: ChangeIndicatorBadge's own sx emits a stray `}` that breaks extra sx rules.
      slotProps={{ badge: { style: { marginTop: "1px" } } }}
    >
      <ElementIcon icon={iconType} sx={{ ...sx, fontSize: "1.125em" }} />
    </ChangeIndicatorBadge>
  );
}

/**
 * Compute the icon for registration capture tree node
 *
 * @param entity entity in the capture tree
 * @param isEditModeEnabled whether edit mode is enabled
 * @returns the icon to use for this element
 */
export function iconForCaptureTreeElement(
  entity: CaptureTreeEntity | undefined,
  isEditModeEnabled: boolean,
): ElementIconType {
  switch (entity?.type) {
    case CaptureTreeEntityType.cluster:
      // Custom icons for special clusters
      switch (entity.typeHint) {
        // Site scape captures are viewed as a single scan from the user perspective
        case CaptureTreeEntityTypeHint.siteScapeCapture:
          return ElementIconType.ScanIcon;
      }
      // Add lock icon for locked clusters
      if (entity.registrationStatus === RegistrationStatus.lockedManually) {
        return ElementIconType.ScanClusterLockIcon;
      }
      // Default cluster icon
      return ElementIconType.ScanClusterIcon;
    case CaptureTreeEntityType.photogrammetryScan:
      // ASSUMPTION: Photogrammetry scan = Drone capture, this is how it's marketed / sold.
      // See https://faro01.atlassian.net/browse/NRT-2901
      return entity.isFixed === true && isEditModeEnabled
        ? ElementIconType.DroneAnchorIcon
        : ElementIconType.DroneScanIcon;
    case CaptureTreeEntityType.orbisScan:
    case CaptureTreeEntityType.roviScan:
      return entity.isFixed === true && isEditModeEnabled
        ? ElementIconType.ScanMobileAnchorIcon
        : ElementIconType.ScanMobileIcon;
    case CaptureTreeEntityType.orbisFlashScan:
      return entity.isFixed === true && isEditModeEnabled
        ? ElementIconType.ScanFlashAnchorIcon
        : ElementIconType.ScanFlashIcon;
    case CaptureTreeEntityType.elsScan:
    case CaptureTreeEntityType.focusScan:
    default:
      return entity?.isFixed === true && isEditModeEnabled
        ? ElementIconType.ScanAnchorIcon
        : ElementIconType.ScanIcon;
  }
}
